import type { PageSideFilters, TopFilters } from '@util/hooks/useProductsPage';
import { slugify } from '@util/urlHelpers';
import { useBikeFilter } from 'context/BikeFilterContext';
import type { Bike } from 'models/shared';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useState } from 'react';

interface UseVehicleForSaleFiltersArgs {
  initialSideFilters: PageSideFilters;
  initialTopFilters: TopFilters;
  /** The for-sale slug used to build the listing URL, e.g. `atvs-for-sale`. */
  forSaleSlug: string;
}

// Owns the listing page's side/top filter state and all the handlers that
// keep it in sync with the URL. Extracted from VehicleForSale so the page
// component stays a slim orchestrator.
export const useVehicleForSaleFilters = ({
  initialSideFilters,
  initialTopFilters,
  forSaleSlug,
}: UseVehicleForSaleFiltersArgs) => {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [topFilters, setTopFilters] = useState<TopFilters>(initialTopFilters);
  const [sideFilters, setSideFilters] =
    useState<PageSideFilters>(initialSideFilters);

  const { setSelectedBike, clearSelectedBike } = useBikeFilter();

  const updateUrl = useCallback(
    (
      newSideFilters: PageSideFilters,
      newTopFilters: TopFilters,
      resetPage = false
    ) => {
      let url = '';
      const urlAdditions: string[] = [];
      const urlSuffixes: string[] = [];
      if (
        newSideFilters.bikeConditions?.length &&
        newSideFilters.bikeConditions?.length === 1
      ) {
        urlAdditions.push(
          slugify(newSideFilters.bikeConditions[0].toLowerCase())
        );
      }
      if (newSideFilters.bikeYears?.length) {
        newSideFilters.bikeYears.forEach((year) => {
          urlAdditions.push(year);
        });
      }
      if (newSideFilters.bikeMakes?.length) {
        newSideFilters.bikeMakes.forEach((make) => {
          urlAdditions.push(slugify(make.toLowerCase()));
        });
      }
      if (newSideFilters.bikeModels?.length) {
        newSideFilters.bikeModels.forEach((model) => {
          urlAdditions.push(slugify(model.toLowerCase()));
        });
      }
      if (newSideFilters.location) {
        urlSuffixes.push(
          '-near-' + slugify(newSideFilters.location.address.toLowerCase())
        );
      }
      urlAdditions.push(forSaleSlug);
      url += urlAdditions.join('-');
      url += urlSuffixes.join('-');

      if (
        (newSideFilters.bikeMakes ?? []).length > 1 ||
        (newSideFilters.shipFrom ?? []).length > 1 ||
        (newSideFilters.shipTo ?? []).length > 1
      ) {
        url = '/filters/' + url;
      }
      if (!url.startsWith('/')) {
        url = '/' + url;
      }
      const query = searchParams;
      const queryParams: string[] = [];
      // Only preserve page param if we're not resetting it (e.g., when changing sort or view)
      if (query && !resetPage) {
        if (query.get('page')) {
          queryParams.push(`page=${query.get('page')}`);
        }
      }
      if (newSideFilters.maxPrice) {
        queryParams.push(`maxPrice=${newSideFilters.maxPrice}`);
      }
      if (newSideFilters.minPrice) {
        queryParams.push(`minPrice=${newSideFilters.minPrice}`);
      }
      if (newSideFilters.sellerType?.length) {
        queryParams.push(`sellerType=${newSideFilters.sellerType.join(',')}`);
      }
      if (newTopFilters.sort && newTopFilters.sort !== 'newest') {
        queryParams.push(`sort=${newTopFilters.sort}`);
      }
      if (newSideFilters.radius) {
        queryParams.push(`radius=${newSideFilters.radius}`);
      }
      if (queryParams.length) {
        if (url.endsWith('/')) {
          url = url.slice(0, -1);
        }
        url += `?${queryParams.join('&')}`;
      }
      router.replace(url, {
        scroll: false,
      });
    },
    [router, searchParams, forSaleSlug]
  );

  const handleSideFiltersChange = useCallback(
    (
      newSideFiltersOrUpdater:
        | PageSideFilters
        | ((prev: PageSideFilters) => PageSideFilters)
    ) => {
      setSideFilters((prev) => {
        const newSideFilters =
          typeof newSideFiltersOrUpdater === 'function'
            ? newSideFiltersOrUpdater(prev)
            : newSideFiltersOrUpdater;
        // Reset page when filters change
        updateUrl(newSideFilters, topFilters, true);
        return newSideFilters;
      });
    },
    [updateUrl, topFilters]
  );

  const onBikeComplete = useCallback(
    (bike: Bike | null) => {
      if (bike === null) {
        handleSideFiltersChange({
          ...sideFilters,
          bikeYears: [],
          bikeMakes: [],
          bikeModels: [],
        });
        clearSelectedBike();
        return;
      }
      if (!bike.year || !bike.make || !bike.model) return;
      handleSideFiltersChange({
        ...sideFilters,
        bikeYears: [`${bike.year}`],
        bikeMakes: [bike.make],
        bikeModels: [bike.model ?? ''],
      });
      setSelectedBike(bike);
    },
    [sideFilters, setSelectedBike, clearSelectedBike, handleSideFiltersChange]
  );

  const handleFilterRemove = useCallback(
    (filter: { key: string; value: unknown }) => {
      const { key, value } = filter;
      const newFilters = { ...sideFilters };
      const current = newFilters[key as keyof typeof newFilters];
      if (Array.isArray(current)) {
        (newFilters[key as keyof typeof newFilters] as unknown[]) =
          current.filter((v) => v !== value);
      } else if (typeof current === 'object' && current !== null) {
        delete (current as Record<string, unknown>)[value as string];
      } else {
        delete newFilters[key as keyof typeof newFilters];
      }
      handleSideFiltersChange(newFilters);
    },
    [sideFilters, handleSideFiltersChange]
  );

  const handleTopFiltersChange = useCallback(
    (newTopFilters: TopFilters) => {
      setTopFilters(newTopFilters);
      updateUrl(sideFilters, newTopFilters);
    },
    [sideFilters, updateUrl]
  );

  const clearAllFilters = useCallback(() => {
    handleSideFiltersChange({
      bikeConditions: [],
      bikeTypes: [],
      bikeYears: [],
      bikeMakes: [],
      bikeModels: [],
      bikeEngineSizes: [],
      sellerType: [],
      location: null,
      minPrice: 0,
      maxPrice: 0,
      conditions: [],
      brands: [],
      numberSizes: [],
      letterSizes: [],
      onsale: false,
    });
  }, [handleSideFiltersChange]);

  return {
    topFilters,
    sideFilters,
    updateUrl,
    handleSideFiltersChange,
    onBikeComplete,
    handleFilterRemove,
    handleTopFiltersChange,
    clearAllFilters,
  };
};
